// S8 — WASTE CAPTURE and S9 — REMNANT INTAKE (spec 05 §4, slice 05-B).
//
// THE 3-TAP PROMISE, kept literally: category grid → cause grid → qty pad.
// Everything else on the screen is either derived (item/WO/work-centre from
// the station context; UoM from the picked job; occurred_at from the tap) or
// optional (photo, note). The grids render from the CACHED catalog — the §5
// unpaginated design exists so this device holds the WHOLE list offline — in
// `sort_order`, which is the §2.2 anti-alphabetical-English guard: a Russian
// floor must never see its buttons ordered by their English codes.
//
// OFFLINE (spec 05 §9): both screens ride the ONE floor outbox (D-048/D-054 —
// generalized to the waste endpoint family in this slice; floor-ops.ts).
// `client_event_id` is the outbox record's tap-time key, so a replay dedupes
// server-side (§9.1) and the evidence photo rides the D-096 binary queue with
// purpose `waste_evidence`, uploaded BEFORE the event is sent.
//
// WHAT THIS SCREEN CANNOT DO, said instead of shelled (D-033):
//  * name its own shift — the server resolves attribution from occurred_at
//    (D-102); a device that could pick whose scoreboard it bills would be the
//    gaming vector spec 04 §3.7 closed;
//  * capture waste with NO job context — the ledger row needs an item, and the
//    honest source of one at a station is the station's queue. An empty queue
//    states that rather than offering a free-text guess.
//  * S9's update-length / move-location: those are module 06 actions on the
//    unit card (/floor/units/:id) — linked, never duplicated.

import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import {
  FloorButton,
  FloorEmpty,
  FloorNotSaved,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { floorPhotoStore, useFloorSubmit } from "../hooks/use-floor-outbox";
import { useCachedRead, floorReadSignal } from "../hooks/use-cached-read";
import { useStationSnapshot } from "../hooks/use-station";
import { capturePhotos, floorPhotoProblem, MAX_FLAG_PHOTOS } from "../lib/photo-upload";
import { localizedName, type StationQueueOperation } from "../lib/production-floor";
import { parseTypedQty } from "../lib/qty-entry";
import type { OutboxSettlement } from "../lib/floor-outbox";
import "./floor-production.css";
import { unitSymbol } from "../../../lib/quantity";

type S = components["schemas"];
type WasteCategory = S["WasteCategory"];
type WasteCauseCode = S["WasteCauseCode"];

interface WasteCatalog {
  readonly categories: readonly WasteCategory[];
  readonly causes: readonly WasteCauseCode[];
}

/** The whole bounded catalog, offline-cached (spec 05 §5 rows 1/61: the floor
 * holds it WHOLE — a paged catalog would be a device with "some of the
 * categories"). Server order is (sort_order, code); never re-sorted here. */
export function useWasteCaptureCatalog(): ReturnType<typeof useCachedRead<WasteCatalog>> {
  return useCachedRead<WasteCatalog>({
    queryKey: ["floor", "waste-catalog"],
    cacheKey: "waste-catalog:all",
    queryFn: async ({ signal }) => {
      const data = unwrap(
        await client.GET("/v1/waste/categories", {
          params: { query: { include: "causes", is_active: "true" } },
          signal: floorReadSignal(signal),
        }),
        "waste-catalog",
      );
      return { categories: data.items, causes: data.causes ?? [] };
    },
  });
}

/** §2.2: causes valid for a category — empty `category_ids` means "all". */
export function causesForCategory(
  causes: readonly WasteCauseCode[],
  categoryId: string,
): readonly WasteCauseCode[] {
  return causes.filter(
    (cause) => cause.category_ids.length === 0 || cause.category_ids.includes(categoryId),
  );
}

type Step = "category" | "cause" | "qty";

export function FloorWasteCaptureScreen() {
  const { t, i18n } = useTranslation();
  const { workCenterId } = useParams({ from: "/floor/station/$workCenterId/waste" });

  const snapshot = useStationSnapshot(workCenterId);
  const catalog = useWasteCaptureCatalog();
  const facility = useStationFacility(workCenterId);
  const { state, submit, reset } = useFloorSubmit();

  const [step, setStep] = useState<Step>("category");
  const [category, setCategory] = useState<WasteCategory | undefined>(undefined);
  const [cause, setCause] = useState<WasteCauseCode | undefined>(undefined);
  const [opId, setOpId] = useState<string | undefined>(undefined);
  const [qty, setQty] = useState("");
  const [locationId, setLocationId] = useState("");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState<readonly File[]>([]);
  const [photoProblem, setPhotoProblem] = useState<string | undefined>(undefined);
  const photoInput = useRef<HTMLInputElement | null>(null);

  const ops = snapshot.data?.queue.operations ?? [];
  // Default job: the running one, else the head of the served order (D-072).
  const defaultOp = ops.find((op) => op.status === "in_progress") ?? ops[0];
  const selectedOp = ops.find((op) => op.id === opId) ?? defaultOp;
  // PRE-WARMED, not lazy (surface-review finding, 2026-08-09): fetching bins
  // only after a dims category was picked meant a first-ever offline dims
  // capture found an empty picker. The list is tiny (§9 — cached whole, like
  // the catalogs) and S9 already fetches it unconditionally; opening this
  // screen online once now warms the cache for airplane mode.
  const locations = useFacilityLocations(facility.facilityId);

  const parsedQty = parseTypedQty(qty, 3);
  const qtyValid = parsedQty !== undefined && Number(parsedQty) > 0;
  const needsDims = category?.requires_dimensions === true;
  const canSubmit =
    category !== undefined &&
    (cause !== undefined || !category.requires_cause) &&
    selectedOp !== undefined &&
    facility.facilityId !== undefined &&
    qtyValid &&
    (!needsDims || locationId.length > 0) &&
    state.phase !== "submitting";

  const onPickPhotos = (files: FileList | null): void => {
    if (files === null) return;
    const picked = [...files].slice(0, MAX_FLAG_PHOTOS);
    for (const file of picked) {
      const problem = floorPhotoProblem(file);
      if (problem !== undefined) {
        setPhotoProblem(problem);
        return;
      }
    }
    setPhotoProblem(undefined);
    setPhotos(picked);
  };

  const doSubmit = async (): Promise<void> => {
    // `canSubmit` is the aliased conjunction — TS narrows category/selectedOp/
    // parsedQty through it; only the property access needs its own re-check.
    if (!canSubmit) return;
    if (facility.facilityId === undefined) return;
    // Blobs BEFORE the flag (D-096): a death between the two leaves an
    // orphaned blob, never an event pointing at nothing.
    let photoIds: string[] = [];
    if (photos.length > 0) {
      try {
        // `waste_evidence`, EXPLICITLY (D-064 pt 8): module 05's ledger only
        // accepts ids stored under this purpose — the default
        // production_evidence would register fine and then park the capture
        // on an unfixable purpose mismatch (the 2026-08-09 defect).
        photoIds = await capturePhotos(floorPhotoStore(), photos, { purpose: "waste_evidence" });
      } catch {
        setPhotoProblem("not_saved");
        return;
      }
    }
    const settlement = await submit(
      {
        kind: "waste_event",
        body: {
          // occurred_at is stamped AT TAP and sent verbatim on every retry —
          // §9.7 clamps server-side; re-stamping would move a night tap into
          // the morning drain.
          occurred_at: new Date().toISOString(),
          facility_id: facility.facilityId,
          source: "floor_report",
          category_id: category.id,
          ...(cause !== undefined ? { cause_code_id: cause.id } : {}),
          item_type: "product",
          item_id: selectedOp.product_id,
          qty: parsedQty,
          uom: needsDims ? "m" : selectedOp.uom,
          work_order_id: selectedOp.work_order_id,
          wo_operation_id: selectedOp.id,
          work_center_id: workCenterId,
          ...(note.length > 0 ? { notes: note } : {}),
          ...(needsDims ? { remnant: { length_m: parsedQty, location_id: locationId } } : {}),
        },
      },
      15_000,
      {
        identity: `${selectedOp.wo_no} · ${category.code}`,
        ...(photoIds.length > 0 ? { photoIds } : {}),
      },
    );
    if (settlement !== undefined) {
      setQty("");
      setNote("");
      setPhotos([]);
    }
  };

  const backToStation = (
    <Link
      to="/floor/station/$workCenterId"
      params={{ workCenterId }}
      className="floor-back"
      data-testid="waste-back"
    >
      {t("waste.floor.backToStation")}
    </Link>
  );

  const categories = catalog.data?.categories ?? [];
  const causes =
    category !== undefined ? causesForCategory(catalog.data?.causes ?? [], category.id) : [];

  return (
    <FloorScreen title={t("waste.floor.captureTitle")} back={backToStation}>
      <SnapshotNote source={catalog.source} cachedAt={catalog.cachedAt} />

      {catalog.isLoading && categories.length === 0 ? <FloorSkeleton rows={4} /> : null}

      {!catalog.isLoading && categories.length === 0 ? (
        <FloorEmpty title={t("waste.floor.noCatalogTitle")} body={t("waste.floor.noCatalogBody")} />
      ) : null}

      {categories.length > 0 && ops.length === 0 && !snapshot.isLoading ? (
        <FloorEmpty title={t("waste.floor.noJobTitle")} body={t("waste.floor.noJobBody")} />
      ) : null}

      {state.phase === "not_saved" ? <FloorNotSaved onRetry={reset} /> : null}
      {state.phase === "settled" ? (
        <SettlementNote settlement={state.settlement} onDone={reset} />
      ) : null}

      {categories.length > 0 && ops.length > 0 ? (
        <>
          {/* Which job takes the hit — derived, one chip row, not a step. */}
          <div className="floor-waste-jobs" data-testid="waste-job-row">
            {ops.map((op) => (
              <button
                key={op.id}
                type="button"
                className={op.id === selectedOp?.id ? "floor-chip floor-chip-active" : "floor-chip"}
                data-testid="waste-job-chip"
                onClick={() => {
                  setOpId(op.id);
                }}
              >
                {op.wo_no} · {op.product_name}
              </button>
            ))}
          </div>

          {step === "category" ? (
            <div className="floor-grid" data-testid="waste-category-grid">
              {categories.map((row) => (
                <FloorButton
                  key={row.id}
                  testId="waste-category-btn"
                  onClick={() => {
                    setCategory(row);
                    setCause(undefined);
                    setStep(row.requires_cause ? "cause" : "qty");
                  }}
                >
                  {localizedName(row.name_i18n, i18n.language, row.code)}
                </FloorButton>
              ))}
            </div>
          ) : null}

          {step === "cause" && category !== undefined ? (
            <div className="floor-grid" data-testid="waste-cause-grid">
              {causes.map((row) => (
                <FloorButton
                  key={row.id}
                  testId="waste-cause-btn"
                  onClick={() => {
                    setCause(row);
                    setStep("qty");
                  }}
                >
                  {localizedName(row.name_i18n, i18n.language, row.code)}
                </FloorButton>
              ))}
              <FloorButton
                variant="ghost"
                testId="waste-cause-back"
                onClick={() => {
                  setStep("category");
                }}
              >
                {t("waste.floor.back")}
              </FloorButton>
            </div>
          ) : null}

          {step === "qty" && category !== undefined ? (
            <div className="floor-waste-qty" data-testid="waste-qty-step">
              <p className="floor-waste-picked">
                {localizedName(category.name_i18n, i18n.language, category.code)}
                {cause !== undefined
                  ? ` · ${localizedName(cause.name_i18n, i18n.language, cause.code)}`
                  : ""}
              </p>
              <label>
                {needsDims
                  ? t("waste.floor.lengthLabel")
                  : t("waste.floor.qtyLabel", {
                      uom: unitSymbol(selectedOp?.uom ?? "", { locale: i18n.language }),
                    })}
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  value={qty}
                  data-testid="waste-qty-input"
                  onChange={(e) => {
                    setQty(e.target.value);
                  }}
                />
              </label>

              {needsDims ? (
                <label>
                  {t("waste.floor.locationLabel")}
                  <select
                    value={locationId}
                    data-testid="waste-location"
                    onChange={(e) => {
                      setLocationId(e.target.value);
                    }}
                  >
                    <option value="">{t("waste.floor.locationPick")}</option>
                    {(locations.data ?? []).map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.code}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <label>
                {t("waste.floor.noteLabel")}
                <input
                  value={note}
                  data-testid="waste-note"
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                />
              </label>

              {/* Optional evidence photo — OS camera, D-096 queue. */}
              <input
                ref={photoInput}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                hidden
                data-testid="waste-photo-input"
                onChange={(e) => {
                  onPickPhotos(e.target.files);
                }}
              />
              <FloorButton
                variant="ghost"
                testId="waste-photo-btn"
                onClick={() => photoInput.current?.click()}
              >
                {photos.length > 0
                  ? t("waste.floor.photosPicked", { n: photos.length })
                  : t("waste.floor.addPhoto")}
              </FloorButton>
              {photoProblem !== undefined ? (
                <p className="floor-state-body" role="alert" data-testid="waste-photo-problem">
                  {t("waste.floor.photoRefused")}
                </p>
              ) : null}

              <FloorButton
                variant="primary"
                testId="waste-submit"
                disabled={!canSubmit}
                onClick={() => {
                  void doSubmit();
                }}
              >
                {state.phase === "submitting"
                  ? t("waste.floor.saving")
                  : t("waste.floor.saveCapture")}
              </FloorButton>
              <FloorButton
                variant="ghost"
                testId="waste-qty-back"
                onClick={() => {
                  setStep(category.requires_cause ? "cause" : "category");
                }}
              >
                {t("waste.floor.back")}
              </FloorButton>
            </div>
          ) : null}
        </>
      ) : null}
    </FloorScreen>
  );
}

// ---------------------------------------------------------------------------
// S9 — remnant intake (register path)
// ---------------------------------------------------------------------------

export function FloorRemnantScreen() {
  const { t } = useTranslation();
  const { workCenterId } = useParams({ from: "/floor/station/$workCenterId/remnant" });

  const snapshot = useStationSnapshot(workCenterId);
  const facility = useStationFacility(workCenterId);
  const { state, submit, reset } = useFloorSubmit();

  const ops = snapshot.data?.queue.operations ?? [];
  const [opId, setOpId] = useState<string | undefined>(undefined);
  const selectedOp: StationQueueOperation | undefined = ops.find((op) => op.id === opId) ?? ops[0];
  const [length, setLength] = useState("");
  const [diameter, setDiameter] = useState("");
  const [locationId, setLocationId] = useState("");
  const locations = useFacilityLocations(facility.facilityId);

  const parsedLength = parseTypedQty(length, 3);
  const parsedDiameter = diameter.length > 0 ? parseTypedQty(diameter, 3) : undefined;
  const canSubmit =
    selectedOp !== undefined &&
    parsedLength !== undefined &&
    Number(parsedLength) > 0 &&
    (diameter.length === 0 || parsedDiameter !== undefined) &&
    locationId.length > 0 &&
    state.phase !== "submitting";

  const doSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    const settlement = await submit(
      {
        kind: "waste_remnant",
        body: {
          kind: "cable_offcut",
          item_type: "product",
          item_id: selectedOp.product_id,
          length_m: parsedLength,
          ...(parsedDiameter !== undefined ? { diameter_mm: parsedDiameter } : {}),
          location_id: locationId,
        },
      },
      15_000,
      { identity: `${selectedOp.product_name} · ${parsedLength} m` },
    );
    if (settlement !== undefined) {
      setLength("");
      setDiameter("");
    }
  };

  return (
    <FloorScreen
      title={t("waste.floor.remnantTitle")}
      back={
        <Link
          to="/floor/station/$workCenterId"
          params={{ workCenterId }}
          className="floor-back"
          data-testid="remnant-back"
        >
          {t("waste.floor.backToStation")}
        </Link>
      }
    >
      {/* Updates to an EXISTING piece are module 06's unit card — linked, not
          duplicated (spec 05 §4 S9's BUILT note). */}
      <p className="floor-waste-hint" data-testid="remnant-existing-hint">
        {t("waste.floor.remnantExistingHint")}{" "}
        <Link to="/floor/scan">{t("waste.floor.remnantScanLink")}</Link>
      </p>

      {state.phase === "not_saved" ? <FloorNotSaved onRetry={reset} /> : null}
      {state.phase === "settled" ? (
        <SettlementNote settlement={state.settlement} onDone={reset} />
      ) : null}

      {ops.length === 0 && !snapshot.isLoading ? (
        <FloorEmpty title={t("waste.floor.noJobTitle")} body={t("waste.floor.noJobBody")} />
      ) : null}

      {ops.length > 0 ? (
        <div className="floor-waste-qty" data-testid="remnant-form">
          <div className="floor-waste-jobs">
            {ops.map((op) => (
              <button
                key={op.id}
                type="button"
                className={op.id === selectedOp?.id ? "floor-chip floor-chip-active" : "floor-chip"}
                data-testid="remnant-job-chip"
                onClick={() => {
                  setOpId(op.id);
                }}
              >
                {op.wo_no} · {op.product_name}
              </button>
            ))}
          </div>
          <label>
            {t("waste.floor.lengthLabel")}
            <input
              inputMode="decimal"
              autoComplete="off"
              value={length}
              data-testid="remnant-length"
              onChange={(e) => {
                setLength(e.target.value);
              }}
            />
          </label>
          <label>
            {t("waste.floor.diameterLabel")}
            <input
              inputMode="decimal"
              autoComplete="off"
              value={diameter}
              data-testid="remnant-diameter"
              onChange={(e) => {
                setDiameter(e.target.value);
              }}
            />
          </label>
          <label>
            {t("waste.floor.locationLabel")}
            <select
              value={locationId}
              data-testid="remnant-location"
              onChange={(e) => {
                setLocationId(e.target.value);
              }}
            >
              <option value="">{t("waste.floor.locationPick")}</option>
              {(locations.data ?? []).map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.code}
                </option>
              ))}
            </select>
          </label>
          <FloorButton
            variant="primary"
            testId="remnant-submit"
            disabled={!canSubmit}
            onClick={() => {
              void doSubmit();
            }}
          >
            {state.phase === "submitting" ? t("waste.floor.saving") : t("waste.floor.saveRemnant")}
          </FloorButton>
        </div>
      ) : null}
    </FloorScreen>
  );
}

// ---------------------------------------------------------------------------
// Shared plumbing
// ---------------------------------------------------------------------------

/** The station's facility — resolved from the work-centre registry (a station
 * pin carries no facility, and the capture body requires one). Cached like
 * every floor read. */
export function useStationFacility(workCenterId: string): {
  facilityId: string | undefined;
} {
  const read = useCachedRead<{ id: string; facility_id: string }[]>({
    queryKey: ["floor", "work-centers-all"],
    cacheKey: "waste-work-centers:all",
    queryFn: async ({ signal }) => {
      const data = unwrap(
        await client.GET("/v1/org/work-centers", { signal: floorReadSignal(signal) }),
        "work-centers",
      );
      return data.items.map((wc) => ({ id: wc.id, facility_id: wc.facility_id }));
    },
  });
  const facilityId = useMemo(
    () => read.data?.find((wc) => wc.id === workCenterId)?.facility_id,
    [read.data, workCenterId],
  );
  return { facilityId };
}

/** The bins the remnant leg may offer: the facility's ACTIVE bins, the server
 * cap of 200 in one read (D-331). An archived bin takes no new movement, so
 * offering one would queue a capture the server parks. */
export function wasteBinsQuery(facilityId: string) {
  return { facility_id: facilityId, kind: "bin", is_active: true, limit: 200 } as const;
}

/** Storage bins for the remnant leg — cached whole per facility. */
function useFacilityLocations(facilityId: string | undefined) {
  return useCachedRead<{ id: string; code: string }[]>({
    queryKey: ["floor", "waste-locations", facilityId ?? null],
    cacheKey: `waste-locations:${facilityId ?? ""}`,
    enabled: facilityId !== undefined,
    queryFn: async ({ signal }) => {
      const data = unwrap(
        await client.GET("/v1/inventory/locations", {
          // The server cap (200) — the picker holds the facility's bins whole;
          // a KazKabel facility has tens, and a tenant that outgrows this gets
          // the search box module 06's browser already has.
          params: { query: wasteBinsQuery(facilityId ?? "") },
          signal: floorReadSignal(signal),
        }),
        "locations",
      );
      return data.items.map((loc) => ({ id: loc.id, code: loc.code }));
    },
  });
}

/** One settlement note — the four honest outcomes, never a silent green. */
function SettlementNote({
  settlement,
  onDone,
}: {
  settlement: OutboxSettlement;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const key =
    settlement.outcome === "applied"
      ? "waste.floor.savedApplied"
      : settlement.outcome === "queued"
        ? "waste.floor.savedQueued"
        : settlement.outcome === "conflicted"
          ? "waste.floor.savedConflicted"
          : settlement.outcome === "needs_attention"
            ? "waste.floor.savedFailed"
            : "waste.floor.savedSuperseded";
  return (
    <div
      className={
        settlement.outcome === "needs_attention" || settlement.outcome === "conflicted"
          ? "floor-state floor-state-error"
          : "floor-state floor-state-empty"
      }
      role="status"
      data-testid={`waste-settled-${settlement.outcome}`}
    >
      <p className="floor-state-title">{t(key)}</p>
      <FloorButton variant="ghost" testId="waste-settled-done" onClick={onDone}>
        {t("waste.floor.captureAnother")}
      </FloorButton>
    </div>
  );
}
